import { useRecoilValue } from 'recoil';
import {
    SaveDICOMToCacheRequestBody,
    useSaveDICOMToCache,
} from '~/backend/connector-api/connector-components';
import { useSessionOrNull } from '~/contexts/SessionContext';
import { environmentState } from '~/state/atoms/engineAtoms';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import { handleError } from '~/utils/errors';
import { buildStructureSetFilename } from '~/utils/io/files/dicomFilename';

/**
 * Handles uploading of structure set for cache update.
 * @returns {Function} - A start function to initiate cache update for current structure set.
 */
export default function useStructureSetCacheUpdate() {
    const currentStructureSet = useRecoilValue(currentStructureSetState);
    const environment = useRecoilValue(environmentState);
    const session = useSessionOrNull();
    const { mutateAsync: saveDICOMToCache } = useSaveDICOMToCache();

    const update = async () => {
        try {
            if (environment && currentStructureSet) {
                environment.saveChanges();

                if (!session) {
                    throw new Error(
                        'No active patient session for cache update.'
                    );
                }
                const structureSetBlob =
                    session.structureSetExportService.exportBlob(
                        currentStructureSet.dicomUid
                    );
                const formData = new FormData();
                formData.append(
                    'files',
                    structureSetBlob,
                    buildStructureSetFilename(
                        currentStructureSet.structureSetLabel,
                        currentStructureSet.dicomUid
                    )
                );
                await saveDICOMToCache({
                    body: formData as unknown as SaveDICOMToCacheRequestBody,
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
            }
        } catch (error) {
            handleError(
                error,
                'Failed to update cached file for structure set.'
            );
        }
    };

    return update;
}
